<template>
    <div>
        <div class="input-group">
            <input v-if="!s3Path" type="file" class="form-control" id="input" 
                @change="handleFileUpload"
                :accept="acceptedFileTypesString"
                :class="{ 'is-invalid': pathError }" />
            <input v-else type="text" class="form-control" id="input" 
                :value="s3Path" 
                placeholder="Select S3 path"
                :class="{ 'is-invalid': pathError }" 
                readonly />
            <button class="btn btn-outline-secondary" type="button" @click="openFileBrowser">
                <span class="ms-1">Browse S3</span>
            </button>
            <button v-if="s3Path" class="btn btn-outline-danger" type="button" @click="clearPath"
                title="Clear path">
                <i class="fa fa-times"></i>
            </button>
        </div>
        <div v-if="pathError" class="invalid-feedback d-block">
            {{ pathError }}
        </div>
    </div>
</template>

<script>
import { mapActions } from 'vuex';

export default {
    name: "BrowseFile",
    props: {
        value: {
            type: Object,
            default: () => ({
                s3Path: '',
                presignedUrl: null,
                localFile: null,
                pathError: '',
                fileContent: null
            })
        },
        acceptedFileTypes: {
            type: Array,
            default: () => ['csv', 'json', 'avro','parquet'],
            validator: function (value) {
                return Array.isArray(value) && value.length > 0;
            }
        },
        generateURL:{
            type: Boolean,
            default: true
        },
        maxFileSize: {
            type: Number,
            default: 5 * 1024 * 1024 // 5MB in bytes
        }
    },
    data() {
        return {
            s3Path: this.value?.s3Path || '',
            pathError: this.value?.pathError || '',
            localFile: this.value?.localFile || null,
            presignedUrl: this.value?.presignedUrl || null,
            fileContent: this.value?.fileContent || null,
            sandbox: this.value?.sandbox || null
        }
    },
    computed: {
        acceptedFileTypesString() {
            return this.acceptedFileTypes.map(type => `.${type}`).join(',');
        },
        isFileSelected() {
            return this.s3Path 
                ? !!(this.presignedUrl && !this.pathError)
                : !!(this.localFile && this.fileContent && !this.pathError);
        }
    },
    watch: {
        s3Path() { this.emitValue(); },
        presignedUrl() { this.emitValue(); },
        localFile() { this.emitValue(); },
        fileContent() { this.emitValue(); },
        pathError() { this.emitValue(); },
        sandbox() { this.emitValue(); },
        value: {
            handler(newVal) {
                if (newVal && typeof newVal === 'object') {
                    if (this.s3Path !== newVal.s3Path) {
                        this.s3Path = newVal.s3Path || '';
                    }
                    if (this.pathError !== newVal.pathError) {
                        this.pathError = newVal.pathError || '';
                    }
                    if (this.localFile !== newVal.localFile) {
                        this.localFile = newVal.localFile || null;
                    }
                    if (this.presignedUrl !== newVal.presignedUrl) {
                        this.presignedUrl = newVal.presignedUrl || null;
                    }
                    if (this.fileContent !== newVal.fileContent) {
                        this.fileContent = newVal.fileContent || null;
                    }
                }
            },
            deep: true,
            immediate: true
        }
    },
    methods: {
        ...mapActions({
            showModal: "showModal"
        }),
        emitValue() {
            const payload = {
                s3Path: this.s3Path,
                presignedUrl: this.presignedUrl,
                sandbox: this.sandbox,
                localFile: this.localFile,
                fileContent: this.fileContent,
                pathError: this.pathError,
                isFileSelected: this.isFileSelected
            };
            this.$emit('input', payload);
        },
        openFileBrowser() {
            this.showModal({
                component: 'FileSelectionModal',
                data: {
                    title: 'Select S3 File',
                    onFileSelected: this.handleS3FileSelection,
                    generateURL:this.generateURL
                }
            });
        },
        validateFileExtension(fileName) {
            const lowerFileName = fileName.toLowerCase();
            const hasValidExtension = this.acceptedFileTypes.some(type => 
                lowerFileName.endsWith(`.${type.toLowerCase()}`)
            );

            if (!hasValidExtension) {
                const extensions = this.acceptedFileTypes.map(type => `.${type.toLowerCase()}`).join(', ');
                this.pathError = `Selected file must be one of the following types: ${extensions}`;
                return false;
            }

            this.pathError = '';
            return true;
        },
        clearPath() {
            this.s3Path = '';
            this.pathError = '';
            this.presignedUrl = null;
        },
        handleS3FileSelection(fileData) {
            this.localFile = null;
            this.fileContent = null;
            this.clearFileInput();
            
            this.s3Path = fileData.s3Path;
            this.presignedUrl = fileData.presignedUrl;
            this.sandbox = fileData.sandbox;
            
            if (!fileData.s3Path) {
                this.pathError = 'Please select a file path';
            } else {
                this.validateFileExtension(fileData.s3Path);
            }
        },
        handleFileUpload(event) {
            const file = event.target.files[0];
            this.localFile = null;
            this.fileContent = null;
            this.pathError = '';
            if (file) {
                this.localFile = file;
                if (this.validateLocalFile(file)) {
                    this.s3Path = '';
                    this.presignedUrl = null;
                    this.readFileContent(file);
                } 
            }
        },
        readFileContent(file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                this.fileContent = e.target.result;
            };
            reader.onerror = () => {
                this.pathError = 'Error reading file';
                this.fileContent = null;
            };
            reader.readAsText(file);
        },
        validateLocalFile(file) {
            if (!file) {
                this.pathError = 'Please select a file';
                return false;
            }

            if (!this.validateFileExtension(file.name)) {
                return false;
            }

            if (file.size > this.maxFileSize) {
                const maxSizeMB = Math.round(this.maxFileSize / (1024 * 1024));
                this.pathError = `File size must be less than ${maxSizeMB}MB`;
                return false;
            }

            this.pathError = '';
            return true;
        },
        clearFileInput() {
            const fileInput = document.getElementById("input");
            if (fileInput && fileInput.type === 'file') {
                fileInput.value = '';
            }
        }
    },
    mounted() {
        this.$nextTick(() => {
            this.emitValue();
        });
    }
};
</script>

<style scoped>
.invalid-feedback.d-block {
    display: block !important;
    color: #dc3545;
    font-size: 0.875rem;
    margin-top: 0.25rem;
}
</style>